import { Component, computed, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import html2canvas from 'html2canvas';
import { CommonModule, NgClass } from '@angular/common';
import { jsPDF } from 'jspdf';
import { Subscription } from 'rxjs';

import { SummaryReportComponent } from './summary-report/summary-report.component';
import { ReportLayoutComponent } from './report-layout/report-layout.component';
import { InteractionReportComponent } from './interaction-report/interaction-report.component';
import { ReportLoaderComponent } from './report-loader.component';
import { CustomerReportService, OverviewReportRequestData } from '../../services/customer-report.service';
import { FilterService } from '../../services/filter.service';
import { BottomSheetDataService } from '../../services/bottom-sheet-data.service';
import {
  InteractionPage,
  SummaryPage,
  SummaryPageChartData,
  SummaryReportGenerationRequest,
  SummaryReportGenerationResponse,
  SummaryReportStatusRequest,
  SummaryReportStatusResponse
} from './report.model';
import { FilterRequest } from '../../shared/types';
import { delayLoading, transformTestToFilters } from '../../shared/appUtil';
import { SharedDataService } from '../../services/shared-data.service';
import { CustomerReportApiService } from '../../services/api-services/customer-report-api.service';
import { NotificationService } from '../../services/notification.service';

@Component({
  selector: 'report',
  standalone: true,
  imports: [
    ReportLayoutComponent,
    InteractionReportComponent,
    SummaryReportComponent,
    ReportLoaderComponent,
    MatIconModule,
    NgClass,
    CommonModule,
    FormsModule,
  ],
  templateUrl: './report.component.html',
  styleUrl: './report.component.scss',
})
export class ReportComponent {
  constructor(
    private filterService: FilterService,
    private customerReportService: CustomerReportService,
    private customerReportApiService: CustomerReportApiService,
    private sharedDataService: SharedDataService,
    public notificationService: NotificationService
  ) {}

  private _bottomSheetDataService = inject(BottomSheetDataService);
  @ViewChild('rightContent') rightContent!: ElementRef<HTMLDivElement>;
  protected readonly interactionPage = computed(() => this.interactionPages()[this.selectedIndex()]);
  hideInfoBoxForever = computed(() => this.customerReportService.hideInfoMsg());
  private subscription: Subscription | undefined;
  protected customerEndpointAddressSelected = computed<boolean>(() =>
    this.filterService.customerEndpointAddressSelected()
  );
  _tempInput = '';
  _characterLimit = 50;
  _hideInfoBoxForever: boolean = false;
  _fetchingApiData = true;
  _pollingReportStatus = false;
  _pollingTimeout: any = null;
  _maxPollingAttempts = 30; // Maximum 30 attempts (60 seconds with 2-second intervals)
  _currentPollingAttempt = 0;
  interactionPages = signal<Array<InteractionPage>>([]);
  selectedIndex = signal<number>(-1);
  summaryPage = signal<SummaryPage>({} as SummaryPage);
  inputName = 'Report';
  inputFocused: boolean = false;
  pdfLoading: boolean = false;
  hideInfoBox: boolean = false;
  disabled = true;
  chartData: SummaryPageChartData = { channelData: [], resolutionData: [] };
  reportRequestData: OverviewReportRequestData = {} as OverviewReportRequestData;

  ngOnInit() {
    this.subscription = this.sharedDataService.getDateRange().subscribe((date) => {
      this.reportRequestData.start_date = date.startAPIDate;
      this.reportRequestData.end_date = date.endAPIDate;
    });

    this.fetchReportData();
  }

    ngOnDestroy() {
    // Stop polling when component is destroyed
    this._pollingReportStatus = false;

    // Clear any pending timeout
    if (this._pollingTimeout) {
      clearTimeout(this._pollingTimeout);
      this._pollingTimeout = null;
    }

    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  async fetchReportData() {
    this.reportRequestData.filters = this.populateFilteredData();
    this.reportRequestData.timeZone = this.sharedDataService.selectedTimezoneValue;

    await delayLoading(1);

    // Step 1: Start summary report generation
    const generationRequest: SummaryReportGenerationRequest = {
      filters: this.reportRequestData.filters,
      start_date: this.reportRequestData.start_date,
      end_date: this.reportRequestData.end_date,
      timeZone: this.reportRequestData.timeZone
    };

    this.customerReportApiService.startSummaryReportGeneration(generationRequest).subscribe({
      next: (response: SummaryReportGenerationResponse) => {
        if (response.success && response.data?.request_id) {
          this._pollingReportStatus = true;
          this._currentPollingAttempt = 0;
          this.pollReportStatus(response.data.request_id);
        } else {
          this._fetchingApiData = false;
          console.error('Failed to start report generation:', response.message);
          // this.notificationService.error('Error', 'Failed to start report generation');
        }
      },
      error: (error) => {
        this._fetchingApiData = false;
        console.error('Failed to start report generation:', error);
        // this.notificationService.error('Error', 'Failed to start report generation');
      }
    });
  }

  private pollReportStatus(requestId: string) {
    if (!this._pollingReportStatus) {
      return;
    }

    this._currentPollingAttempt++;

    // Check if we've exceeded the maximum polling attempts
    if (this._currentPollingAttempt > this._maxPollingAttempts) {
      this._pollingReportStatus = false;
      this._fetchingApiData = false;

      // Clear any pending timeout
      if (this._pollingTimeout) {
        clearTimeout(this._pollingTimeout);
        this._pollingTimeout = null;
      }

      console.error('Report generation timeout after maximum attempts');
      this.notificationService.error('Error', 'Report generation timeout. Please try again.');
      return;
    }

    const statusRequest: SummaryReportStatusRequest = {
      request_id: requestId
    };

    this.customerReportApiService.getSummaryReportStatus(statusRequest).subscribe({
      next: (response: SummaryReportStatusResponse) => {
        if (response.success) {
          if (response.data.status === 'completed' && response.data.report_data) {
            // Report generation completed successfully
            this._pollingReportStatus = false;
            this._fetchingApiData = false;

            // Clear any pending timeout
            if (this._pollingTimeout) {
              clearTimeout(this._pollingTimeout);
              this._pollingTimeout = null;
            }

            const data = response.data.report_data;
            this.interactionPages.set(this.customerReportService.createInteractionPages(data));
            this.summaryPage.set(this.customerReportService.createSummaryPage(data));
            this.chartData.channelData = this.customerReportService.transformDataToPieChartData(
              data.channelDistribution.channels,
              data.channelDistribution.channelPercentages
            );
            this.chartData.resolutionData = this.customerReportService.transformDataToPieChartData(
              data.resolutionDistribution.resolutions,
              data.resolutionDistribution.resolutionPercentages
            );

          } else if (response.data.status === 'in_progress') {
            // Report still in progress, poll again after 2 seconds
            this._pollingTimeout = setTimeout(() => {
              this.pollReportStatus(requestId);
            }, 2000);
          } else if (response.data.status === 'failed') {
            // Report generation failed
            this._pollingReportStatus = false;
            this._fetchingApiData = false;

            // Clear any pending timeout
            if (this._pollingTimeout) {
              clearTimeout(this._pollingTimeout);
              this._pollingTimeout = null;
            }

            console.error('Report generation failed');
            this.notificationService.error('Error', 'Report generation failed');
          } else {
            // Unknown status
            this._pollingReportStatus = false;
            this._fetchingApiData = false;

            // Clear any pending timeout
            if (this._pollingTimeout) {
              clearTimeout(this._pollingTimeout);
              this._pollingTimeout = null;
            }

            console.error('Unknown report status:', response.data.status);
            this.notificationService.error('Error', 'Unknown report generation status');
          }
        } else {
          this._pollingReportStatus = false;
          this._fetchingApiData = false;

          // Clear any pending timeout
          if (this._pollingTimeout) {
            clearTimeout(this._pollingTimeout);
            this._pollingTimeout = null;
          }

          console.error('Failed to get report status:', response.message);
          this.notificationService.error('Error', 'Failed to get report status');
        }
      },
      error: (error) => {
        this._pollingReportStatus = false;
        this._fetchingApiData = false;

        // Clear any pending timeout
        if (this._pollingTimeout) {
          clearTimeout(this._pollingTimeout);
          this._pollingTimeout = null;
        }

        console.error('Failed to get report status:', error);
        this.notificationService.error('Error', 'Failed to get report status');
      }
    });
  }

  populateFilteredData(): FilterRequest[] {
    const filterDataArray = this.filterService.getFilterConditionDataValue();
    const { data, error } = transformTestToFilters(filterDataArray);
    const filterApplied = this.filterService.getFilterAppliedStatusValue();

    const filteredData: FilterRequest[] = [...data];
    if (error && filterApplied) {
      // this.notificationService.error('Error in filters', 'Error in filters');
    }
    return filteredData;
  }

  scrollToDescription(index: number): void {
    let element = this.rightContent.nativeElement.querySelector(`#page${index}`);
    if (index === -1) {
      element = this.rightContent.nativeElement.querySelector(`#summaryPage`);
    }
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  handleInfoClosePress() {
    if (this._hideInfoBoxForever) {
      this.customerReportService.setHideInfoMsg(true);
    } else {
      this.hideInfoBox = true;
    }
  }

  handleSavePress() {
    this.inputName = this._tempInput;
    this.disabled = true;
  }

  checkValue(event: any) {
    if (
      event.target.value == this.inputName ||
      event.target.value.length > this._characterLimit ||
      event.target.value.length === 0
    ) {
      this.disabled = true;
    } else {
      this.disabled = false;
    }
    this._tempInput = event.target.value;
  }

  handleCheckBoxPress() {
    this._hideInfoBoxForever = !this._hideInfoBoxForever;
  }

  async capturePages(): Promise<void> {

    if (this.inputName.trim().length === 0) {
      this.notificationService.error('Invalid Input', 'Please enter a valid report name.');
      return;
    }

    // Prevent multiple simultaneous PDF generations
    if (this.pdfLoading) {
      return;
    }

    // Check if we have data to generate PDF
    if (!this.summaryPage() || !this.summaryPage().dateRange) {
      this.notificationService.error('No Data', 'No report data available to generate PDF.');
      return;
    }

    this.pdfLoading = true;

    // Show continuous "Downloading Report..." toaster
    const downloadingToaster = this.notificationService.processing('Downloading Report...', 'Downloading Report...', 0);

    try {
      await this.generatePDF();
      // Dismiss the downloading toaster
      downloadingToaster.dismiss();
      // Show success message
      this.notificationService.success('Report Downloaded.', 'Report Downloaded.');
    } catch (error) {
      console.error('Error during PDF generation:', error);
      // Dismiss the downloading toaster
      downloadingToaster.dismiss();
      this.notificationService.error('PDF Generation Failed', 'Error occurred. Please try again.');
    } finally {
      this.pdfLoading = false;
    }
  }

  private async generatePDF(): Promise<void> {
    // Add a small delay to ensure UI is updated and elements are rendered
    await new Promise(resolve => setTimeout(resolve, 500));

    const pageIds = ['summaryPage', ...this.interactionPages().map((_, index) => `page${index}`)];

    const pdf = new jsPDF('p', 'px', [612, 792], true);

    let pagesCaptured = 0;
    const maxRetries = 3;

    for (let i = 0; i < pageIds.length; i++) {
      let element = document.getElementById(pageIds[i]) as HTMLElement;

      // If element not found, try to find it in the right content area
      if (!element && this.rightContent) {
        element = this.rightContent.nativeElement.querySelector(`#${pageIds[i]}`) as HTMLElement;
      }

      if (!element) {
        console.error('Element not found with id:', pageIds[i]);
        continue;
      }

      // Wait for element to be fully rendered
      let retryCount = 0;
      while ((element.offsetWidth === 0 || element.offsetHeight === 0) && retryCount < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 200));
        retryCount++;
      }

      if (element.offsetWidth === 0 || element.offsetHeight === 0) {
        // console.warn(`Element ${pageIds[i]} still has zero dimensions after retries, skipping`);
        continue;
      }

      // Check if element is visible
      const rect = element.getBoundingClientRect();

      try {
        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false, // Disable html2canvas logging to reduce console noise
          width: element.offsetWidth,
          height: element.offsetHeight,
          scrollX: 0,
          scrollY: 0,
          onclone: (clonedDoc) => {
            // Ensure cloned elements are visible
            const clonedElement = clonedDoc.getElementById(pageIds[i]);
            if (clonedElement) {
              (clonedElement as HTMLElement).style.display = 'block';
              (clonedElement as HTMLElement).style.visibility = 'visible';
            }
          }
        });

        const imgData = canvas.toDataURL('image/png');

        // Add each image to a new page, but avoid adding a page before the first image
        if (i > 0) {
          pdf.addPage();
        }

        pdf.addImage(imgData, 'PNG', 0, 0, 612, 792, undefined, 'FAST');
        pagesCaptured++;
      } catch (error) {
        console.error('Error capturing element with id:', pageIds[i], error);
        // Continue with next page instead of stopping
      }
    }


    if (pagesCaptured > 0) {
      pdf.save(`${this.inputName}.pdf`);
      this.notificationService.success('PDF Generated', 'PDF has been generated.');
    } else {
      console.error('No pages were captured successfully');
      this.notificationService.error('PDF Generation Failed', 'No pages could be captured. Please try again.');
    }
  }

  handlePreviewPress(index: number, type = 'interaction') {
    if (type === 'interaction') {
      this.selectedIndex.set(index);
    } else {
      this.selectedIndex.set(-1);
    }
    this.scrollToDescription(index);
  }

  handleChooseFilterPress() {
    this._bottomSheetDataService.setBottomSheetExpandedState('minimized');
    this.filterService.handleCustomerEndpointAddress();
  }
}
